import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, LogOut, X, FileText, Shield, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { removeAuthToken } from '../../utils/auth';

const ANIMATION_DURATION = 300;

export default function UserMenu({ className = '' }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // Track if sheet should be rendered (stays true during close animation)
  const [shouldRender, setShouldRender] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle mount/unmount with animation
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- managing render state for animation
      setShouldRender(true);
    } else if (shouldRender) {
      // Delay unmount to allow close animation to complete
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  const handleLogout = () => {
    removeAuthToken();
    window.location.href = '/login';
  };

  // Navigate with animation - close sheet first, then navigate after animation
  const handleNavigate = useCallback((path) => {
    // If already on this page, just close the menu
    if (location.pathname === path) {
      setIsOpen(false);
      return;
    }

    // Close menu and navigate after animation completes
    setIsOpen(false);
    setTimeout(() => {
      navigate(path);
    }, ANIMATION_DURATION);
  }, [location.pathname, navigate]);

  return (
    <>
      {/* Hamburger menu button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`flex items-center justify-center w-9 h-9 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors ${className}`}
        aria-label="Menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Bottom sheet - only render when needed */}
      {shouldRender && createPortal(
        <>
          {/* Backdrop - extends above viewport to cover iOS status bar */}
          <div
            className={`fixed inset-x-0 bottom-0 z-[200] bg-black/50 transition-opacity duration-300 ${
              isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            style={{
              // Use fixed -100px to reliably cover iOS status bar on all pages
              top: '-100px',
            }}
            onClick={() => setIsOpen(false)}
          />

          {/* Sheet */}
          <div
            className={`fixed inset-x-0 bottom-0 z-[201] transform transition-transform duration-300 ease-out ${
              isOpen ? 'translate-y-0' : 'translate-y-full'
            }`}
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-t-2xl">
              {/* Handle bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="font-semibold text-gray-900 dark:text-white">{t('menu.title')}</span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center w-8 h-8 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Navigation Section */}
              <div className="py-2 px-4">
                <p className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('menu.navigation')}
                </p>
                <button
                  onClick={() => handleNavigate('/')}
                  className="flex items-center gap-3 w-full px-4 py-3 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                >
                  <Home className="w-5 h-5" />
                  <span>{t('nav.home')}</span>
                </button>
              </div>

              {/* Legal Section */}
              <div className="py-2 px-4 border-t border-gray-100 dark:border-gray-700">
                <p className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('menu.legal')}
                </p>
                <button
                  onClick={() => handleNavigate('/tos')}
                  className="flex items-center gap-3 w-full px-4 py-3 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                >
                  <FileText className="w-5 h-5" />
                  <span>{t('menu.termsOfUse')}</span>
                </button>
                <button
                  onClick={() => handleNavigate('/privacy')}
                  className="flex items-center gap-3 w-full px-4 py-3 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                >
                  <Shield className="w-5 h-5" />
                  <span>{t('menu.privacyPolicy')}</span>
                </button>
              </div>

              {/* Account Section - at bottom */}
              <div className="py-2 px-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg font-medium transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span>{t('nav.logout')}</span>
                </button>
              </div>

              {/* Bottom padding for safe area */}
              <div className="h-4" />
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
